import { SubmitClaim } from "commonAssets/components/Forms/SubmitClaim";
import { ErrorHandler } from "commonAssets/utils/ErrorHandler";
import { createElement } from "commonAssets/lib/createElement";
import { loadingGif } from "commonAssets/lib/loadingGif";
import {
    loading, outputDiv, i18n, appConfigDev,
    appConfigKey,
    appConfigProd,
    appPayMethod,
    appDomain,
    claimForm,
    claimMethod,
    reCAPTCHAv3,
    userLanguage,
} from "commonAssets/js/constants";
import formDefault from "../../docs/formDefault.json";
import claimDeadlineAlertBody from "../../docs/html/claim_deadline_alert_body.html?raw";
import claimDeadlineAlertForm from "../../docs/html/claim_deadline_alert_form.html?raw";
import generalInstructionsBody from "../../docs/html/general_instructions_body.html?raw";
import contactInformationBody from "../../docs/html/contact_information_body.html?raw";
import { modal } from "commonAssets/lib/modal";
import { validateField } from "commonAssets/lib/validateField";
import { clearMessage } from "commonAssets/lib/clearMessage";
import { toggleFieldError } from "commonAssets/lib/toggleFieldError";
import { uniformClaimDeadline } from "commonAssets/lib/uniformClaimDeadline";
import { formatPhone } from "commonAssets/lib/formatPhone";
import { toggleFieldAttributeV2 } from "commonAssets/lib/toggleFieldAttributeV2";
import { toggleForeignPhone } from "commonAssets/lib/toggleForeignPhone";
import { DigitalDisbursements } from "commonAssets/components/FormBase/DigitalDisbursements";
import {
    FormFields,
    FormFieldsLabel,
} from "commonAssets/components/FormBase/FormFields";
import { resolveHtmlFields } from "commonAssets/lib/resolveHtmlFields";
import { classMemberAuth, writeNlcmAutoLogin } from "./classMemberAuth.mjs";

const htmlAssets = {
    "./html/claim_deadline_alert_body.html": claimDeadlineAlertBody,
    "./html/claim_deadline_alert_form.html": claimDeadlineAlertForm,
    "./html/general_instructions_body.html": generalInstructionsBody,
    "./html/contact_information_body.html": contactInformationBody,
};

export class customSubmitClaim extends SubmitClaim {
    async form() {
        const baseUrl = new URL(
            "../../docs/formDefault.json",
            window.location.href
        ).href;
        const config = await SubmitClaim.resolveHtmlFields(formDefault, {
            baseUrl,
            assets: htmlAssets,
        });
        const form = config.claim_form[this.lang];
        this.init_claim(form);
    }

    init_claim(form) {
        this.claim.api
            .claimsDeadlineGet({ domain: this.claim.data.domain })
            .then((result) => {
                // Claim Deadline - Default Pacific DateTime
                let datetime;
                if (Boolean(result.data.pacific_datetime)) {
                    datetime = result.data.pacific_datetime;
                } else {
                    datetime = `<pre>Error: datetime</pre>`;
                }

                let claim_deadline;
                if (Boolean(result.data.claim_deadline)) {
                    claim_deadline = result.data.claim_deadline;
                }

                // Error Handling
                if (result.data.error) {
                    this.setup_result_data_error(result);
                    return;
                }

                const container = createElement("div", {
                    class: "display-form",
                });

                /**
                 * Claim Guard
                 */
                if (form.form_options.notice_confirmation.display === true) {
                    // Guard
                    container.appendChild(this.guard(form, datetime));
                    outputDiv.appendChild(container);

                    const zero_zero_id =
                        document.getElementById("zero_zero_id");
                    const confirmation_code =
                        document.getElementById("confirmation_code");
                    const skip_guard = document.getElementById("skip-guard");

                    // Notice Id and Confirmation Code
                    [zero_zero_id, confirmation_code].forEach(function (input) {
                        input.addEventListener("change", validateField);
                    });

                    // Skip Guard
                    if (skip_guard) {
                        skip_guard.addEventListener("click", () => {
                            // Hide guard
                            // document.getElementById('guard').classList.add("d-none");
                            document.getElementById("guard").remove();
                            // New Claim Form
                            container.appendChild(
                                this.claim_form(form, datetime)
                            );
                            container.scrollIntoView({ behavior: "smooth" });
                            outputDiv.appendChild(container);

                            // Init claim fields
                            this.init_claim_fields(
                                this.claim,
                                form,
                                claim_deadline
                            );
                        });
                    }

                    // ada compliance updates - added
                    // if input#submit-guard was clicked add .was-verified to form#guard
                    document
                        .getElementById("submit-guard")
                        .addEventListener("click", () => {
                            document
                                .getElementById("guard")
                                .classList.add("was-validated");
                            // TODO: add required text to labels
                        });

                    // Validate Guard Fields
                    document
                        .getElementById("guard")
                        .addEventListener("submit", (e) => {
                            e.preventDefault();

                            let valid = true;
                            const member = {
                                domain: this.claim.data.domain,
                                zero_zero_id: zero_zero_id.value.toUpperCase(),
                                confirmation_code: confirmation_code.value,
                            };

                            clearMessage();

                            // Validate guard fields
                            [zero_zero_id, confirmation_code].forEach(
                                function (input) {
                                    valid = validateField(input) && valid;
                                }
                            );
                            if (!valid) return;

                            loadingGif(loading, "show");

                            // Retrieve class member data (use POST to pass notice id and confirmation code privately)
                            this.claim.api
                                .classMemberPost({}, member)
                                .then((result) => {
                                    // Check for error message
                                    if (result.data.error) {
                                        toggleFieldError(
                                            zero_zero_id,
                                            true,
                                            ""
                                        );
                                        toggleFieldError(
                                            confirmation_code,
                                            true,
                                            ""
                                        );
                                        this.errorHandler.log_exception(
                                            new Error(
                                                "Error loading class member",
                                                { cause: result }
                                            )
                                        );
                                        this.errorHandler.display_modal(result);
                                        return;
                                    }

                                    // Add notice id and confirmation code to claim data
                                    this.claim.data.zero_zero_id =
                                        member.zero_zero_id;
                                    this.claim.data.confirmation_code =
                                        member.confirmation_code;

                                    // Define the environment for the OTP
                                    const environment =
                                        this.claim.data.domain.split(".")[0]; // ada compliance - added

                                    const auth = classMemberAuth(result.data, 'payment_election');
                                    if (auth.action === 'redirect') {
                                        writeNlcmAutoLogin(member.zero_zero_id, member.confirmation_code);
                                        window.location.assign(auth.target);
                                        return;
                                    }
                                    if (auth.action === 'error') {
                                        modal('Error', 'You are not eligible to proceed with the payment election form. If you believe this is an error, please contact support.');
                                        loadingGif(loading, "hide", 250);
                                        return;
                                    }

                                    if (result.data.claim_id) {
                                        this.claim.data.claim_id =
                                            result.data.claim_id;
                                        this.claim.class_member = result.data;
                                        if (environment !== "local") {
                                            this.send_otp(
                                                form,
                                                container,
                                                result.data,
                                                claim_deadline
                                            );
                                            return;
                                        }
                                    }

                                    // Populate claim form
                                    this.populate_claim_form(
                                        form,
                                        container,
                                        result.data,
                                        claim_deadline
                                    );
                                })
                                .catch((error) => {
                                    this.errorHandler.log_exception(
                                        new Error(
                                            "Error loading class member",
                                            { cause: error }
                                        )
                                    );
                                    this.errorHandler.display_modal(error);
                                });
                        });
                } else {
                    /**
                     * No Claim Guard
                     */
                    // Edit claim link
                    this.edit_claim(form);

                    // New Claim Form
                    const deadlineSpan = uniformClaimDeadline(claim_deadline);
                    container.appendChild(this.claim_form(form, deadlineSpan));
                    outputDiv.appendChild(container);

                    // Init claim fields
                    this.init_claim_fields(this.claim, form, claim_deadline);
                }

                loadingGif(loading, "hide", 250);
            })
            .catch((error) => {
                this.errorHandler.log_exception(
                    new Error("Error fetching claim deadline", { cause: error })
                );
                this.errorHandler.display(error);
            });
    }

    static createInstance({
        appClaimForm,
        userLanguage,
        customInitClaim = null,
        customProcessClaim = null,
        customLoadMember = null,
        customDstProcess = null,
        errorHandler = new ErrorHandler(),
    }) {
        return new this(
            appClaimForm,
            userLanguage,
            customInitClaim,
            customProcessClaim,
            customLoadMember,
            customDstProcess,
            errorHandler
        );
    }

    guard(params, getDeadline) {
        let form;
        if (params.form_options.generic_claim.display) {
            // If the Guard allows Generic Claims
            form = createElement(
                "form",
                {
                    class: "needs-validation",
                    id: "guard",
                    method: "post",
                },
                [
                    createElement(
                        "div",
                        { class: "row fs-5", id: "guardRow" },
                        [
                            this.notice_confirmation(params),
                            this.notice_generic(params),
                        ]
                    ),
                    this.claim_deadline(params, getDeadline),
                ]
            );
        } else {
            // For closed classes
            form = createElement(
                "form",
                {
                    class: "needs-validation",
                    id: "guard",
                    method: "post",
                },
                [
                    createElement(
                        "div",
                        { class: "row fs-5", id: "guardRow" },
                        [
                            this.notice_confirmation(
                                params,
                                "col-md-6 offset-md-3 mb-5"
                            ),
                        ]
                    ),
                    //this.claim_deadline(params, getDeadline),
                ]
            );
        }
        return form;
    }

    claim_form(params, additionalParams) {
        console.log("[claim_form]");
        // update the claim form deadline
        const deadline_heading =
            params.form_options.claim_deadline_alert.claim_form;
        const deadline_parser = new DOMParser();
        const deadline_string = deadline_parser.parseFromString(
            deadline_heading,
            "text/html"
        );
        const deadline_date_string = deadline_string.querySelector("span");
        // if deadline_date_string is null, then the deadline is not set
        // the if condition was added due to error in i18n translation
        // TODO: remove this if condition after fixing the i18n translation
        if (deadline_date_string !== null) {
            deadline_date_string.textContent = additionalParams;
        }

        // const the form
        const form = createElement(
            "form",
            {
                class: "needs-validation",
                id: "form",
                method: "post",
            },
            [
                // createElement(
                //     "h3",
                //     {
                //         class: "text-center mb-3",
                //         id: "claimFormDeadline",
                //     },
                //     [],
                //     deadline_string.body.innerHTML
                // ),
                createElement(
                    "div",
                    {
                        class: "alert alert-info text-center fw-bold text-dark",
                        id: "confirmation-email-alert",
                        role: "alert",
                    },
                    [],
                    params.form_options.claim_form.confirmation_email_notice
                ),
                this.claim_instructions(params),
                this.claim_contact_information(params),
                this.claim_fieldset(params),
                params.claim_transactions
                    ? this.claim_transactions(params)
                    : ``,
                params.claim_upload.documentation.required
                    ? this.claim_documentation(params)
                    : ``,
                appPayMethod ? DigitalDisbursements(params) : ``,
                this.claim_certification(params),
            ]
        );
        return form;
    }

    destroy_claim(claim) {
        loadingGif(loading, "hide", 500); // left here

        if (claim.flags.success) {
            claim.data.confirmation = this.claim_data_confirmation(claim.data);
            document.querySelector("div.display-form").classList.add("d-none");

            outputDiv.appendChild(this.claim_confirmation(claim.data));
            outputDiv.scrollIntoView({ behavior: "smooth" });

            //JSB
            fbq('track', 'Lead');
            gtag_report_conversion();

            setTimeout(() => {
                modal(
                    i18n.getTranslation("confirmation_screen_additional")[
                    "title"
                    ],
                    i18n.getTranslation("confirmation_screen_additional")[
                    "body"
                    ]
                );
            }, 250);
        } else {
            modal(
                i18n.getTranslation("error"),
                i18n.getTranslation("claim_submit_error")
            );
        }

        // Loop thru each input and disable to prevent changes and resubmissions
        for (const type in claim.inputs) {
            if (claim.inputs[type] && claim.inputs[type].length > 0) {
                claim.inputs[type].forEach(function (field) {
                    field.disabled = true;
                });
            }
        }

        [].forEach.call(
            document.querySelectorAll('input[type="radio"]'),
            function (radio) {
                radio.setAttribute("disabled", "disabled");
            }
        );

        document
            .getElementById("submit-claim")
            .setAttribute("disabled", "disabled");
    }
}
